import React, { useCallback, useEffect, useState, useRef, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ShoppingBag, X } from 'lucide-react';
// --- Types ---
interface ScannedProduct {
  title: string;
  editionLabel: string;
  priceNumber: number;
  priceText: string;
  thumbnailUrl: string | null;
}
interface Notification {
  id: number;
  name: string;
  city: string;
  timeAgo: string;
  product: ScannedProduct;
}
// --- Spanish female names (55+ demographic) ---
const NAMES = ['María García', 'Carmen López', 'Rosa Martínez', 'Ana Rodríguez', 'Teresa Hernández', 'Dolores Pérez', 'Isabel Sánchez', 'Francisca Ramírez', 'Josefa Torres', 'Antonia Flores', 'Pilar Díaz', 'Consuelo Morales', 'Esperanza Reyes', 'Guadalupe Cruz', 'Lucía Ortiz', 'Elena Gutiérrez', 'Marta Ruiz', 'Beatriz Mendoza', 'Gloria Vargas', 'Patricia Castillo', 'Silvia Romero', 'Alicia Jiménez', 'Graciela Medina', 'Estela Herrera', 'Norma Aguilar', 'Yolanda Castro', 'Irma Vega', 'Blanca Guerrero', 'Margarita Molina', 'Susana Delgado', 'Claudia Ríos', 'Adriana Suárez', 'Luz Marina Parra', 'Cecilia Navarro', 'Amparo Domínguez', 'Rosario Campos', 'Elvira Peña', 'Nelly Contreras', 'Olga Fuentes', 'Lidia Salazar'];
// --- Latin American & Spanish-speaking cities ---
const CITIES = ['Ciudad de México', 'Bogotá', 'Buenos Aires', 'Lima', 'Santiago', 'Guadalajara', 'Medellín', 'Monterrey', 'Quito', 'Caracas', 'San José', 'Panamá', 'Santo Domingo', 'La Habana', 'Montevideo', 'Puebla', 'Barranquilla', 'Córdoba', 'Guayaquil', 'Arequipa', 'Cancún', 'Cartagena', 'Valparaíso', 'Cusco', 'Mérida', 'Miami', 'Los Ángeles', 'Houston', 'Chicago', 'Nueva York', 'Madrid', 'Barcelona', 'Sevilla', 'Valencia'];
const TIME_AGO_OPTIONS = ['hace 2 minutos', 'hace 5 minutos', 'hace 8 minutos', 'hace 12 minutos', 'hace 15 minutos', 'hace 20 minutos', 'hace 25 minutos', 'hace 30 minutos', 'hace 1 hora', 'hace 2 horas'];
// --- Product Scanner ---
function scanProducts(): ScannedProduct[] {
  const products: ScannedProduct[] = [];
  // Strategy 1: Look for JSON-LD structured data
  try {
    const scripts = document.querySelectorAll('script[type="application/ld+json"]');
    scripts.forEach(script => {
      const data = JSON.parse(script.textContent || '');
      if (data['@type'] === 'Product' && data.offers) {
        const offer = Array.isArray(data.offers) ? data.offers[0] : data.offers;
        products.push({
          title: data.name || 'Producto',
          editionLabel: '',
          priceNumber: parseFloat(offer.price) || 7,
          priceText: `$${offer.price}`,
          thumbnailUrl: data.image || null
        });
      }
    });
  } catch {
    // JSON-LD parsing failed, continue
  }
  // Strategy 2: Scan DOM for price patterns and nearby product info
  if (products.length === 0) {
    const priceRegex = /\$\s?(\d+(?:\.\d{1,2})?)/g;
    const allText = document.body.innerText || '';
    const priceMatches = new Set<number>();
    let match;
    while ((match = priceRegex.exec(allText)) !== null) {
      priceMatches.add(parseFloat(match[1]));
    }
    // Look for product images
    const productImages = document.querySelectorAll('img[alt*="ebook"], img[alt*="libro"], img[alt*="Fuego"], img[alt*="portada"], img[alt*="Portada"]');
    const thumbnailUrl = productImages.length > 0 ? (productImages[0] as HTMLImageElement).src : null;
    // Look for the main product title
    const headings = document.querySelectorAll('h1, h2, h3');
    let productTitle = 'El Fuego que Nadie Ve';
    headings.forEach(h => {
      const text = h.textContent?.trim() || '';
      if (text.includes('FUEGO') || text.includes('Fuego')) {
        productTitle = text.replace(/\n/g, ' ').trim();
      }
    });
    // If we found the $7 price on the page, use it as the main product
    if (priceMatches.has(7)) {
      products.push({
        title: productTitle,
        editionLabel: 'Manual Digital — 55 páginas',
        priceNumber: 7,
        priceText: '$7',
        thumbnailUrl
      });
    }
  }
  return products;
}
// --- Fallback products ---
function getFallbackProducts(): ScannedProduct[] {
  return [{
    title: 'El Fuego que Nadie Ve',
    editionLabel: 'Manual Digital — 55 páginas',
    priceNumber: 7,
    priceText: '$7',
    thumbnailUrl: null
  }];
}
// --- Helper: pick random from array, avoiding recent history ---
function pickRandom<T>(arr: T[], history: T[], historyMax: number): T {
  const available = arr.filter(item => !history.includes(item));
  const pool = available.length > 0 ? available : arr;
  const pick = pool[Math.floor(Math.random() * pool.length)];
  history.push(pick);
  if (history.length > historyMax) history.shift();
  return pick;
}
function randomInterval(min: number, max: number): number {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}
// --- Component ---
export function RecentPurchaseToast() {
  const [notification, setNotification] = useState<Notification | null>(null);
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const productsRef = useRef<ScannedProduct[]>([]);
  const nameHistoryRef = useRef<string[]>([]);
  const counterRef = useRef(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const generateNotification = useCallback((): Notification => {
    const products = productsRef.current;
    const product = products[Math.floor(Math.random() * products.length)];
    const name = pickRandom(NAMES, nameHistoryRef.current, 10);
    const city = CITIES[Math.floor(Math.random() * CITIES.length)];
    const timeAgo = TIME_AGO_OPTIONS[Math.floor(Math.random() * TIME_AGO_OPTIONS.length)];
    counterRef.current += 1;
    return {
      id: counterRef.current,
      name,
      city,
      timeAgo,
      product
    };
  }, []);
  const showNotification = useCallback(() => {
    if (dismissed) return;
    const notif = generateNotification();
    setNotification(notif);
    setVisible(true);
    // Hide after 4 seconds
    setTimeout(() => {
      setVisible(false);
    }, 4000);
    // Schedule next
    const nextDelay = randomInterval(15000, 25000);
    timerRef.current = setTimeout(showNotification, nextDelay);
  }, [generateNotification, dismissed]);
  useEffect(() => {
    // Scan products on mount
    const scanned = scanProducts();
    productsRef.current = scanned.length > 0 ? scanned : getFallbackProducts();
    // First notification after 3 seconds
    timerRef.current = setTimeout(showNotification, 3000);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [showNotification]);
  const handleDismiss = () => {
    setVisible(false);
    setDismissed(true);
    if (timerRef.current) clearTimeout(timerRef.current);
  };
  return <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 w-[90vw] max-w-md pointer-events-none" data-id="element-209">
      <AnimatePresence data-id="element-210">
        {visible && notification && <motion.div key={notification.id} initial={{
        y: 80,
        opacity: 0,
        scale: 0.95
      }} animate={{
        y: 0,
        opacity: 1,
        scale: 1
      }} exit={{
        y: 80,
        opacity: 0,
        scale: 0.95
      }} transition={{
        type: 'spring',
        damping: 25,
        stiffness: 300
      }} className="pointer-events-auto bg-white rounded-2xl shadow-2xl border border-gray-100 p-4 flex gap-4 items-center relative" data-id="element-211">
            {/* Dismiss button */}
            <button onClick={handleDismiss} className="absolute top-2 right-2 text-gray-300 hover:text-gray-500 transition-colors p-1" aria-label="Cerrar notificación" data-id="element-212">
              <X size={14} data-id="element-213" />
            </button>

            {/* Thumbnail */}
            <div className="w-14 h-14 rounded-xl bg-cream flex-shrink-0 overflow-hidden flex items-center justify-center border border-gold/20" data-id="element-214">
              {notification.product.thumbnailUrl ? <img src={notification.product.thumbnailUrl} alt={notification.product.title} className="w-full h-full object-cover" data-id="element-215" /> : <ShoppingBag className="text-sage" size={24} data-id="element-216" />}
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0 pr-4" data-id="element-217">
              <p className="text-sm font-bold text-text-black truncate" data-id="element-218">
                {notification.name}
              </p>
              <p className="text-xs text-text-gray truncate" data-id="element-219">
                {notification.city} · {notification.timeAgo}
              </p>
              <p className="text-xs text-sage font-medium mt-1 truncate" data-id="element-220">
                Compró {notification.product.title}
              </p>
            </div>

            {/* Price badge */}
            <div className="flex-shrink-0 bg-sage/10 text-sage font-bold text-sm px-3 py-1.5 rounded-lg" data-id="element-221">
              {notification.product.priceText}
            </div>

            {/* Live indicator dot */}
            <div className="absolute -top-1 -right-1 w-3 h-3" data-id="element-222">
              <span className="absolute inset-0 rounded-full bg-green-400 animate-ping opacity-75" data-id="element-223" />
              <span className="absolute inset-0 rounded-full bg-green-500" data-id="element-224" />
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}